import React, { useState, useCallback, useMemo } from "react";
import { useGet, usePost, usePut, useDelete } from "../../api";
import {
  IAffiliateAdvertiser,
  IAdvertisersResponse,
  IProductsResponse,
  IAffiliateProduct,
  ICreateManualAdvertiserRequest,
  IUpdateManualAdvertiserRequest,
  ICreateManualProductRequest,
  IUpdateManualProductRequest,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Modal,
  Form,
  Row,
  Col,
  Card,
  Alert,
} from "react-bootstrap";
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from "react-feather";

// ── Advertiser Form ─────────────────────────────────────────

interface AdvertiserFormData {
  advertiserId: string;
  name: string;
  region: string;
  websiteUrl: string;
  domain: string;
  description: string;
  imageUrl: string;
  currency: string;
}

const EMPTY_ADVERTISER: AdvertiserFormData = {
  advertiserId: "",
  name: "",
  region: "",
  websiteUrl: "",
  domain: "",
  description: "",
  imageUrl: "",
  currency: "",
};

interface AdvertiserModalProps {
  show: boolean;
  advertiser: IAffiliateAdvertiser | null;
  onHide: () => void;
  onSaved: () => void;
}

const AdvertiserModal: React.FC<AdvertiserModalProps> = ({
  show,
  advertiser,
  onHide,
  onSaved,
}) => {
  const isEdit = !!advertiser;
  const [form, setForm] = useState<AdvertiserFormData>(EMPTY_ADVERTISER);
  const [error, setError] = useState<string | null>(null);

  const post = usePost<IAffiliateAdvertiser, ICreateManualAdvertiserRequest>();
  const put = usePut<IAffiliateAdvertiser, IUpdateManualAdvertiserRequest>();

  React.useEffect(() => {
    if (show) {
      setError(null);
      if (advertiser) {
        setForm({
          advertiserId: advertiser.advertiserId,
          name: advertiser.name,
          region: advertiser.region || "",
          websiteUrl: advertiser.websiteUrl || "",
          domain: advertiser.domain || "",
          description: advertiser.description || "",
          imageUrl: advertiser.imageUrl || "",
          currency: advertiser.currency || "",
        });
      } else {
        setForm(EMPTY_ADVERTISER);
      }
    }
  }, [show, advertiser]);

  const handleChange = (field: keyof AdvertiserFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      if (isEdit) {
        const body: IUpdateManualAdvertiserRequest = {
          name: form.name || undefined,
          region: form.region || undefined,
          websiteUrl: form.websiteUrl || undefined,
          domain: form.domain || undefined,
          description: form.description || undefined,
          imageUrl: form.imageUrl || undefined,
          currency: form.currency || undefined,
        };
        await put.execute(
          `affiliate-products/advertisers/Manual/${advertiser!.advertiserId}`,
          body
        );
      } else {
        const body: ICreateManualAdvertiserRequest = {
          name: form.name,
          advertiserId: form.advertiserId || undefined,
          region: form.region || undefined,
          websiteUrl: form.websiteUrl || undefined,
          domain: form.domain || undefined,
          description: form.description || undefined,
          imageUrl: form.imageUrl || undefined,
          currency: form.currency || undefined,
        };
        await post.execute("affiliate-products/advertisers", body);
      }
      onSaved();
      onHide();
    } catch (err: any) {
      const msg =
        err?.data?.error ||
        err?.data?.message ||
        err?.message ||
        "Unknown error";
      setError(msg);
    }
  };

  const loading = post.loading || put.loading;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Manual Advertiser" : "Add Manual Advertiser"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
          {!isEdit && (
            <Col md={6}>
              <Form.Label className="small">
                Advertiser ID{" "}
                <span className="text-muted">(optional — auto-generated)</span>
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="my-store-id"
                value={form.advertiserId}
                onChange={(e) => handleChange("advertiserId", e.target.value)}
              />
            </Col>
          )}
          <Col md={isEdit ? 12 : 6}>
            <Form.Label className="small">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              size="sm"
              placeholder="My Store"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Domain</Form.Label>
            <Form.Control
              size="sm"
              placeholder="mystore.com"
              value={form.domain}
              onChange={(e) => handleChange("domain", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Region</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GB"
              value={form.region}
              onChange={(e) => handleChange("region", e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Currency</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GBP"
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Website URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://mystore.com"
              value={form.websiteUrl}
              onChange={(e) => handleChange("websiteUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Image / Logo URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://mystore.com/logo.png"
              value={form.imageUrl}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              placeholder="A store we track manually."
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || !form.name.trim()}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Advertiser"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Product Form ────────────────────────────────────────────

interface ProductFormData {
  name: string;
  description: string;
  categories: string;
  price: string;
  originalPrice: string;
  salePrice: string;
  currency: string;
  inStock: boolean;
  stockQuantity: string;
  imageUrl: string;
  productUrl: string;
  merchantUrl: string;
  merchantDomain: string;
  manufacturer: string;
  networkManufacturerId: string;
  sku: string;
  ean: string;
  condition: string;
  color: string;
  size: string;
}

const EMPTY_PRODUCT: ProductFormData = {
  name: "",
  description: "",
  categories: "",
  price: "",
  originalPrice: "",
  salePrice: "",
  currency: "",
  inStock: true,
  stockQuantity: "",
  imageUrl: "",
  productUrl: "",
  merchantUrl: "",
  merchantDomain: "",
  manufacturer: "",
  networkManufacturerId: "",
  sku: "",
  ean: "",
  condition: "",
  color: "",
  size: "",
};

interface ProductModalProps {
  show: boolean;
  advertiserId: string;
  product: IAffiliateProduct | null;
  onHide: () => void;
  onSaved: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  show,
  advertiserId,
  product,
  onHide,
  onSaved,
}) => {
  const isEdit = !!product;
  const [form, setForm] = useState<ProductFormData>(EMPTY_PRODUCT);
  const [error, setError] = useState<string | null>(null);

  const post = usePost<IAffiliateProduct, ICreateManualProductRequest>();
  const put = usePut<IAffiliateProduct, IUpdateManualProductRequest>();

  React.useEffect(() => {
    if (show) {
      setError(null);
      if (product) {
        setForm({
          name: product.name,
          description: product.description || "",
          categories: (product.categories || []).join(", "),
          price: product.price != null ? String(product.price) : "",
          originalPrice:
            product.originalPrice != null ? String(product.originalPrice) : "",
          salePrice: product.salePrice != null ? String(product.salePrice) : "",
          currency: product.currency || "",
          inStock: product.inStock ?? true,
          stockQuantity:
            product.stockQuantity != null ? String(product.stockQuantity) : "",
          imageUrl: product.imageUrl || "",
          productUrl: product.productUrl || "",
          merchantUrl: product.merchantUrl || "",
          merchantDomain: (product as any).merchantDomain || "",
          manufacturer: product.manufacturer || "",
          networkManufacturerId: product.networkManufacturerId || "",
          sku: product.sku || "",
          ean: product.ean || "",
          condition: product.condition || "",
          color: product.color || "",
          size: product.size || "",
        });
      } else {
        setForm(EMPTY_PRODUCT);
      }
    }
  }, [show, product]);

  const handleChange = (
    field: keyof ProductFormData,
    value: string | boolean
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildBody = (): ICreateManualProductRequest => {
    const cats = form.categories
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      name: form.name,
      description: form.description || undefined,
      categories: cats.length ? cats : undefined,
      price: form.price !== "" ? Number(form.price) : undefined,
      originalPrice:
        form.originalPrice !== "" ? Number(form.originalPrice) : undefined,
      salePrice: form.salePrice !== "" ? Number(form.salePrice) : undefined,
      currency: form.currency || undefined,
      inStock: form.inStock,
      stockQuantity:
        form.stockQuantity !== "" ? Number(form.stockQuantity) : undefined,
      imageUrl: form.imageUrl || undefined,
      productUrl: form.productUrl || undefined,
      merchantUrl: form.merchantUrl || undefined,
      merchantDomain: form.merchantDomain || undefined,
      manufacturer: form.manufacturer || undefined,
      networkManufacturerId: form.networkManufacturerId || undefined,
      sku: form.sku || undefined,
      ean: form.ean || undefined,
      condition: form.condition || undefined,
      color: form.color || undefined,
      size: form.size || undefined,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      const body = buildBody();
      if (isEdit) {
        await put.execute(
          `affiliate-products/products/${advertiserId}/${encodeURIComponent(product!.id)}`,
          body
        );
      } else {
        await post.execute(`affiliate-products/products/${advertiserId}`, body);
      }
      onSaved();
      onHide();
    } catch (err: any) {
      const msg =
        err?.data?.error ||
        err?.data?.message ||
        err?.message ||
        "Unknown error";
      setError(msg);
    }
  };

  const loading = post.loading || put.loading;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Manual Product" : "Add Manual Product"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
          <Col md={12}>
            <Form.Label className="small">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              size="sm"
              placeholder="Blue Summer Dress"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">
              Categories <span className="text-muted">(comma-separated)</span>
            </Form.Label>
            <Form.Control
              size="sm"
              placeholder="Clothing, Dresses"
              value={form.categories}
              onChange={(e) => handleChange("categories", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              placeholder="34.99"
              value={form.price}
              onChange={(e) => handleChange("price", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Original Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              placeholder="49.99"
              value={form.originalPrice}
              onChange={(e) => handleChange("originalPrice", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Sale Price</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              step={0.01}
              placeholder="34.99"
              value={form.salePrice}
              onChange={(e) => handleChange("salePrice", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Currency</Form.Label>
            <Form.Control
              size="sm"
              placeholder="GBP"
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Manufacturer</Form.Label>
            <Form.Control
              size="sm"
              value={form.manufacturer}
              onChange={(e) => handleChange("manufacturer", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Manufacturer ID</Form.Label>
            <Form.Control
              size="sm"
              value={form.networkManufacturerId}
              onChange={(e) =>
                handleChange("networkManufacturerId", e.target.value)
              }
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">SKU</Form.Label>
            <Form.Control
              size="sm"
              value={form.sku}
              onChange={(e) => handleChange("sku", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">EAN</Form.Label>
            <Form.Control
              size="sm"
              value={form.ean}
              onChange={(e) => handleChange("ean", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Condition</Form.Label>
            <Form.Select
              size="sm"
              value={form.condition}
              onChange={(e) => handleChange("condition", e.target.value)}
            >
              <option value="">—</option>
              <option value="New">New</option>
              <option value="Used">Used</option>
              <option value="Refurbished">Refurbished</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small">Color</Form.Label>
            <Form.Control
              size="sm"
              value={form.color}
              onChange={(e) => handleChange("color", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Size</Form.Label>
            <Form.Control
              size="sm"
              value={form.size}
              onChange={(e) => handleChange("size", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Stock Quantity</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              value={form.stockQuantity}
              onChange={(e) => handleChange("stockQuantity", e.target.value)}
            />
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              label="In Stock"
              checked={form.inStock}
              onChange={(e) => handleChange("inStock", e.target.checked)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Image URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={form.imageUrl}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Product URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={form.productUrl}
              onChange={(e) => handleChange("productUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Merchant URL</Form.Label>
            <Form.Control
              size="sm"
              placeholder="https://..."
              value={form.merchantUrl}
              onChange={(e) => handleChange("merchantUrl", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Merchant Domain</Form.Label>
            <Form.Control
              size="sm"
              placeholder="mystore.com"
              value={form.merchantDomain}
              onChange={(e) => handleChange("merchantDomain", e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || !form.name.trim()}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Product"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Advertiser Products Row ─────────────────────────────────

interface AdvertiserRowProps {
  advertiser: IAffiliateAdvertiser;
  onEdit: (adv: IAffiliateAdvertiser) => void;
  onDelete: (adv: IAffiliateAdvertiser) => void;
}

const AdvertiserRow: React.FC<AdvertiserRowProps> = ({
  advertiser,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] =
    useState<IAffiliateProduct | null>(null);
  const [productVersion, setProductVersion] = useState(0);

  const productsUrl = useMemo(() => {
    const params = new URLSearchParams({
      advertiserId: advertiser.advertiserId,
      network: "Manual",
      limit: "100",
      offset: "0",
    });
    return `affiliate-products/products?${params.toString()}`;
  }, [advertiser.advertiserId, productVersion]);

  const products = useGet<IProductsResponse>(
    expanded ? productsUrl : null,
    expanded
  );

  const deleteProduct = useDelete<void>();
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null
  );

  const handleDeleteProduct = async (product: IAffiliateProduct) => {
    if (
      !window.confirm(
        `Delete product "${product.name}"? This cannot be undone.`
      )
    )
      return;
    setDeletingProductId(product.id);
    try {
      await deleteProduct.execute(
        `affiliate-products/products/${advertiser.advertiserId}/${encodeURIComponent(product.id)}`
      );
      setProductVersion((v) => v + 1);
    } catch {
      // error surfaced via deleteProduct.error
    } finally {
      setDeletingProductId(null);
    }
  };

  const productList = products.data?.products ?? [];

  return (
    <>
      <tr>
        <td style={{ width: 32 }}>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => setExpanded((e) => !e)}
            title={expanded ? "Collapse" : "Expand products"}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td>
          <strong>{advertiser.name}</strong>
          {advertiser.domain && (
            <small className="text-muted ms-2">{advertiser.domain}</small>
          )}
        </td>
        <td>
          <code className="small">{advertiser.advertiserId}</code>
        </td>
        <td>{advertiser.region || "—"}</td>
        <td>{advertiser.currency || "—"}</td>
        <td>
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="outline-secondary"
              title="Edit"
              onClick={() => onEdit(advertiser)}
            >
              <Edit2 size={12} />
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              title="Delete"
              onClick={() => onDelete(advertiser)}
            >
              <Trash2 size={12} />
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <div className="bg-light border-top border-bottom px-4 py-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="small">
                  Products{" "}
                  {products.data && (
                    <Badge bg="secondary" className="ms-1">
                      {products.data.totalCount}
                    </Badge>
                  )}
                </strong>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setProductVersion((v) => v + 1)}
                    disabled={products.loading}
                    title="Refresh"
                  >
                    <RefreshCw size={12} />
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      setEditingProduct(null);
                      setShowProductModal(true);
                    }}
                  >
                    <Plus size={12} className="me-1" />
                    Add Product
                  </Button>
                </div>
              </div>
              {products.loading && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {products.error && (
                <Alert variant="danger" className="py-1 small">
                  {products.error.message}
                </Alert>
              )}
              {deleteProduct.error && (
                <Alert variant="danger" className="py-1 small">
                  {deleteProduct.error.message}
                </Alert>
              )}
              {!products.loading && productList.length === 0 && (
                <p className="text-muted small mb-1">No products yet.</p>
              )}
              {productList.length > 0 && (
                <Table size="sm" hover className="mb-0 bg-white">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Manufacturer</th>
                      <th>Price</th>
                      <th>In Stock</th>
                      <th>Categories</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {productList.map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{p.manufacturer || "—"}</td>
                        <td>
                          {p.price != null
                            ? `${p.currency || ""}${p.price}`
                            : "—"}
                          {p.isOnSale && (
                            <Badge
                              bg="success"
                              className="ms-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              Sale
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge bg={p.inStock ? "success" : "secondary"}>
                            {p.inStock ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>
                          <small>
                            {(p.categories || []).join(", ") || "—"}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setEditingProduct(p);
                                setShowProductModal(true);
                              }}
                              title="Edit"
                            >
                              <Edit2 size={11} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteProduct(p)}
                              disabled={deletingProductId === p.id}
                              title="Delete"
                            >
                              {deletingProductId === p.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Trash2 size={11} />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </td>
        </tr>
      )}
      <ProductModal
        show={showProductModal}
        advertiserId={advertiser.advertiserId}
        product={editingProduct}
        onHide={() => setShowProductModal(false)}
        onSaved={() => setProductVersion((v) => v + 1)}
      />
    </>
  );
};

// ── Main Page ───────────────────────────────────────────────

export const ManualEntry: React.FC = () => {
  const [showAdvertiserModal, setShowAdvertiserModal] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] =
    useState<IAffiliateAdvertiser | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const advertisers = useGet<IAdvertisersResponse>(
    "affiliate-products/advertisers?network=Manual&limit=200&offset=0"
  );
  const advertiserList = advertisers.data?.advertisers ?? [];

  const deleteAdvertiser = useDelete<void>();

  const handleDeleteAdvertiser = useCallback(
    async (adv: IAffiliateAdvertiser) => {
      if (
        !window.confirm(
          `Delete advertiser "${adv.name}" and all its products? This cannot be undone.`
        )
      )
        return;
      setDeleteError(null);
      try {
        await deleteAdvertiser.execute(
          `affiliate-products/advertisers/Manual/${adv.advertiserId}`
        );
        await advertisers.refetch();
      } catch (err: any) {
        const msg =
          err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error";
        setDeleteError(msg);
      }
    },
    [advertisers, deleteAdvertiser]
  );

  const handleEditAdvertiser = useCallback((adv: IAffiliateAdvertiser) => {
    setEditingAdvertiser(adv);
    setShowAdvertiserModal(true);
  }, []);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          Manual Entry{" "}
          <Badge bg="secondary" style={{ fontSize: "0.7rem" }}>
            Manual
          </Badge>
        </h4>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => advertisers.refetch()}
            disabled={advertisers.loading}
          >
            <RefreshCw size={14} className="me-1" />
            Refresh
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              setEditingAdvertiser(null);
              setShowAdvertiserModal(true);
            }}
          >
            <Plus size={14} className="me-1" />
            Add Advertiser
          </Button>
        </div>
      </div>

      <p className="text-muted small mb-3">
        Manage advertisers and products that are not sourced from any affiliate
        network. Expand an advertiser row to view and manage its products.
      </p>

      {deleteError && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setDeleteError(null)}
        >
          {deleteError}
        </Alert>
      )}

      {advertisers.error && (
        <Alert variant="danger">{advertisers.error.message}</Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {advertisers.loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading advertisers...
            </div>
          ) : advertiserList.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No manual advertisers yet. Click <strong>Add Advertiser</strong>{" "}
              to get started.
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Name</th>
                  <th>Advertiser ID</th>
                  <th>Region</th>
                  <th>Currency</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {advertiserList.map((adv) => (
                  <AdvertiserRow
                    key={adv.id}
                    advertiser={adv}
                    onEdit={handleEditAdvertiser}
                    onDelete={handleDeleteAdvertiser}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <AdvertiserModal
        show={showAdvertiserModal}
        advertiser={editingAdvertiser}
        onHide={() => setShowAdvertiserModal(false)}
        onSaved={() => advertisers.refetch()}
      />
    </div>
  );
};
